import React, { memo, useEffect, useState } from 'react';
import { useField, useFormikContext } from 'formik';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { nanoid } from 'nanoid';
import { Spinner } from 'reactstrap';
import classNames from 'classnames';
import { MODALS } from '@/configs/modals';
import {
  addPsychologistDiploma,
  deletePsychologistDiploma,
} from '@/store/psychologist/actions';
import { getDiplomasSrc } from '@/utils/helpers';
import { useModals } from '@/hooks/useModals';
import FieldWrapper from './FieldWrapper';

const FormikDiplomasUpload = memo((props) => {
  const {
    name,
    label,
    className,
    fieldClassName,
    accept = 'image/*',
    isUpdateMode,
    psychologistId,
  } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { modalOpen } = useModals();
  const [fieldInit] = useField(name);
  const { setFieldValue } = useFormikContext();
  const [imagesList, setImagesList] = useState(fieldInit?.value);

  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    const readers = files.map((file) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          const readerData = {
            file: reader.result,
          };
          if (isUpdateMode) {
            readerData._id = nanoid();
            readerData.isLoading = true;
          }
          return resolve(readerData);
        }
      });
    });

    Promise.all(readers).then((images) => {
      const imagesListNew = [...(imagesList || []), ...(images || [])];
      setFieldValue(name, imagesListNew);

      if (isUpdateMode) {
        images?.forEach((image) => {
          addPsychologistDiploma({
            data: {
              _id: image?._id,
              file: image?.file,
              psychologist: +psychologistId,
            },
            dispatch,
            onSuccess: (res) => {
              setImagesList((prev) =>
                prev?.map((i) => (i._id === res?._id ? res : i))
              );
            },
            onError: (err) => {
              setImagesList((prev) =>
                prev?.map((i) =>
                  i._id === err?._id
                    ? {
                        ...i,
                        isLoading: undefined,
                        error: i?.detail || 'Помилка зображення',
                      }
                    : i
                )
              );
            },
          });
        });
      }
    });

    event.target.value = '';
  };

  useEffect(() => {
    setImagesList(fieldInit?.value);
  }, [fieldInit?.value]);

  const handleClickRemoveImage = (index, diplomId) => {
    modalOpen(MODALS.confirm, {
      isOpen: true,
      text: t('psychologist.diplomas.confirm_delete'),
      isDeleteType: true,
      onOk: () => {
        if (isUpdateMode && diplomId) {
          dispatch(
            deletePsychologistDiploma(diplomId, {
              onSuccess: () => {
                removeImage(index);
              },
              t,
            })
          );
        } else {
          removeImage(index);
        }
      },
    });
  };

  const removeImage = (index) => {
    const imagesListNew = imagesList?.filter((_, idx) => idx !== index);
    setFieldValue(name, imagesListNew);
  };

  const handleClickOpenLightbox = (itemIdx) => {
    modalOpen(MODALS.lightbox, {
      isOpen: true,
      images: imagesList?.map((i) => getDiplomasSrc(i, 'original')),
      initIndex: itemIdx,
    });
  };

  return (
    <FieldWrapper
      name={name}
      label={label}
      fieldClassName={fieldClassName}
      className={className}
    >
      <div className={`diplomas-uploader ${isUpdateMode ? 'update-mode' : ''}`}>
        <div className="diplomas-list">
          {imagesList.map((image, index) => (
            <div
              className={classNames('diplomas-list-item', {
                'is-error': !!image?.error,
              })}
              key={index}
            >
              <img
                className="diplomas-list-item-preview"
                src={getDiplomasSrc(image)}
                alt="uploaded"
                onClick={() => handleClickOpenLightbox(index)}
              />
              {!!image?.isLoading && (
                <div className="diplomas-list-item-spinner">
                  <Spinner color="primary" type="grow" />
                </div>
              )}
              {!!image?.error && (
                <div className="diplomas-list-item-error">
                  <i className="fas fa-exclamation-triangle diplomas-list-item-error__tooltip-btn" />
                  <span className="diplomas-list-item-error__tooltip-text">
                    {image?.error}
                  </span>
                </div>
              )}
              <button
                className="diplomas-list-item-btn-remove"
                onClick={(e) => {
                  e?.preventDefault();
                  handleClickRemoveImage(index, image?.id);
                }}
                type="button"
              >
                ✕
              </button>
            </div>
          ))}
          <label className="diplomas-list-btn-add">
            <input
              type="file"
              multiple
              accept={accept}
              onChange={handleImageUpload}
              style={{ display: 'none' }}
            />
            +
          </label>
        </div>
      </div>
    </FieldWrapper>
  );
});

export default FormikDiplomasUpload;
